import React, { useCallback, useEffect, useState } from "react";
import { Button, Col, Empty, Form, Input, Row, Space } from "antd";
import { useHistory, useParams } from "react-router";
import { unwrapResult } from "@reduxjs/toolkit";
import { includes, map, size, split } from "lodash";
import { useAppDispatch } from "app/store";
import { DisplayInput, Modal, message } from "components";
import { CustomerAiccInfo } from "types/model/aiccCustomer";
import { CustomerModel } from "types/model/customer";
import { CustomerInfoResponse } from "types/dto/response/customer";
import * as aiccAPI from "services/aiccCustomer";
import { DEFAULT_SUB_USER_NAME } from "types/transformers/aiccCustomer";
import { boldAndItalic } from "utils";
import { useAICCStore } from "../../aicc/useAICCStore";
import { fetchCustomerAiccInfo } from "../customerSlice";
import styles from "./AICC.module.scss";

interface Props {
  customer?: CustomerModel & CustomerInfoResponse;
}

const getSubUserDisplayId = (id: string) => (includes(id, "_") ? split(id, "_")[1] : id);

function AICC({ customer }: Props) {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { customerId } = useParams<{ customerId: string }>();
  const [aiccInfo, setAiccInfo] = useState<CustomerAiccInfo>();
  const [isHistoryDeleted, setIsHistoryDeleted] = useState(false);
  const setCachedSubUserDetail = useAICCStore((state) => state.setCachedSubUserDetail);

  const loadAiccInfo = useCallback(() => {
    if (!customerId) {
      return;
    }
    dispatch(fetchCustomerAiccInfo(customerId))
      .then(unwrapResult)
      .then(setAiccInfo)
      .catch(() => setAiccInfo(undefined));
  }, [dispatch, customerId]);

  useEffect(() => {
    loadAiccInfo();
  }, [loadAiccInfo]);

  const { isAllAccess, messageQuotaUsed, messageQuotaTotal } = aiccInfo ?? {};

  const quotaUsageLabel = isAllAccess ? "Daily usage" : "Total usage";

  const handleSubUserClick = (subUserId: string) => {
    history.push(`/aicc/customer/subusers/${subUserId}`);
  };

  const handleResetQuotas = () => {
    Modal.confirm({
      title: "Reset Quotas",
      content: isAllAccess
        ? `Are you sure you want to reset today's quota for this All Access User to ${messageQuotaTotal}?`
        : `Are you sure you want to reset the total usable quota for this Free User to ${messageQuotaTotal}?`,
      okText: "Reset",
      okButtonProps: { shape: "round", style: { minWidth: "92px" } },
      cancelButtonProps: { shape: "round" },
      onOk: () =>
        aiccAPI.resetCustomerQuotas(customerId).then(() => {
          message.success("Reset quotas successfully.");
          loadAiccInfo();
        }),
    });
  };

  const customerEmail = customer?.email ?? "";
  const subUserCount = size(customer?.subUserDetails);

  const handleDeleteAllConversations = () => {
    // Follows the customer-deletion dialog in CustomerList/CustomerAction.tsx: the
    // okButton starts disabled and `modal.update` re-enables it once the typed text
    // matches the email exactly, which is how this repo does a typed confirmation.
    const modal = Modal.confirm({
      title: "Confirm",
      content: (
        <Space direction="vertical" size={12}>
          {`This action will permanently delete all historical conversations for all ${subUserCount} Sub-users under this Customer, and cannot be undone.`}
          {boldAndItalic`To confirm deletion, type ${customerEmail}.`}
          <Input
            onChange={(e) =>
              modal.update((prevConfig) => ({
                ...prevConfig,
                okButtonProps: {
                  ...prevConfig.okButtonProps,
                  disabled: e.target.value !== customerEmail,
                },
              }))
            }
          />
        </Space>
      ),
      okText: "Delete",
      okButtonProps: {
        disabled: true,
        shape: "round",
        style: { minWidth: "92px" },
      },
      onOk: () =>
        aiccAPI.deleteCustomerConversations(customerId).then(() => {
          message.success("Deleted all conversation history successfully.");
          // Sub-user conversation counts are now stale in the shared cache.
          setCachedSubUserDetail(undefined);
          setIsHistoryDeleted(true);
        }),
    });
  };

  return (
    <div className={styles.aiccSection}>
      {aiccInfo ? (
        <Space size={24} direction="vertical" style={{ width: "100%" }}>
          <Form layout="vertical">
            <Row gutter={32}>
              <Col span={8}>
                <Form.Item label="Customer ID">
                  <DisplayInput value={customerId} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Subuser Name">
                  <DisplayInput
                    render={() => (
                      <div className={styles.subUsers}>
                        {map(customer?.subUserDetails, ({ id, name }) => (
                          <Button
                            key={id}
                            type="link"
                            className={styles.subUserLink}
                            onClick={() => handleSubUserClick(getSubUserDisplayId(id))}
                          >
                            {`${name || DEFAULT_SUB_USER_NAME} (${getSubUserDisplayId(id)})`}
                          </Button>
                        ))}
                      </div>
                    )}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Current Customer Type">
                  <DisplayInput value={isAllAccess ? "All Access User" : "Free User"} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label={`Current Message Quota (${quotaUsageLabel})`}>
                  <DisplayInput
                    render={() => (
                      <div className={styles.paragraph}>
                        <span>{`${messageQuotaUsed}/${messageQuotaTotal}`}</span>
                        <Button type="default" shape="round" size="small" onClick={handleResetQuotas}>
                          Reset Quotas
                        </Button>
                      </div>
                    )}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form>

          <div>
            <Button danger shape="round" disabled={isHistoryDeleted} onClick={handleDeleteAllConversations}>
              Delete All Conversation History
            </Button>
          </div>
        </Space>
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
      )}
    </div>
  );
}

export default AICC;
